import type { ComponentProps, ReactElement } from 'react';

import { TabsNav } from './TabsNav';
import { TabPanel } from './TabPanel';

export interface TabsProps {
  /**
   * Tabs content. Use `Tabs.Nav` and `Tabs.Panel` components.
   */
  children: (
    | ReactElement<ComponentProps<typeof TabsNav>, 'TabsNav'>
    | ReactElement<ComponentProps<typeof TabPanel>, 'TabPanel'>
  )[];
  /**
   * Styles override.
   */
  className?: string;
}

/**
 * Tabs are used to organize content by grouping similar information on the same page.
 *
 * {@link https://zeroheight.com/76c439891/p/1901d5-tabs Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-tabs Code documentation}
 *
 * @example
 * <Tabs description="List of Tabs">
 *   <Tabs.Nav>
 *     <Tabs.Tab isSelected>Tab 1</Tabs.Tab>
 *   </Tabs.Nav>
 *   <Tabs.Panel isVisible>Content for tab 1</Tabs.Panel>
 * </Tabs>
 */
export const Tabs = ({ children, className }: TabsProps): JSX.Element => {
  return <div className={className}>{children}</div>;
};

Tabs.Nav = TabsNav;
Tabs.Tab = TabsNav.Tab;
Tabs.Panel = TabPanel;
